import React from "react";

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";


/* =========================================================
   IGNITE SKILL CENTER
   PROFESSIONAL PAYROLL PDF
========================================================= */

const COLORS = {
  orange: "#F97316",
  orangeDark: "#C2410C",
  orangeSoft: "#FFF7ED",

  gold: "#D99A16",
  goldSoft: "#FFFBEA",

  charcoal: "#1F2937",
  text: "#111827",
  muted: "#6B7280",

  background: "#F8FAFC",
  white: "#FFFFFF",

  border: "#E5E7EB",
  borderLight: "#F1F5F9",

  green: "#15803D",
  greenSoft: "#F0FDF4",

  red: "#B91C1C",
  redSoft: "#FEF2F2",
};


/* =========================================================
   STYLES
========================================================= */

const styles = StyleSheet.create({

  page: {
    paddingTop: 28,
    paddingBottom: 30,
    paddingHorizontal: 30,
    backgroundColor: COLORS.background,
    fontSize: 9,
    color: COLORS.text,
  },

  landscapePage: {
    paddingTop: 25,
    paddingBottom: 28,
    paddingHorizontal: 28,
    backgroundColor: COLORS.background,
    fontSize: 8,
    color: COLORS.text,
  },


  /* =======================================================
     DOCUMENT HEADER
  ======================================================= */

  documentHeader: {
    backgroundColor: COLORS.white,
    borderTop: `4 solid ${COLORS.orange}`,
    borderBottom: `1 solid ${COLORS.border}`,
    padding: 14,
    marginBottom: 16,
  },

  headerTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },

  schoolName: {
    fontSize: 18,
    fontWeight: "bold",
    color: COLORS.charcoal,
  },

  schoolSubtitle: {
    marginTop: 4,
    fontSize: 9,
    color: COLORS.muted,
  },

  officialBadge: {
    backgroundColor: COLORS.orangeSoft,
    border: `1 solid ${COLORS.orange}`,
    borderRadius: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },

  officialBadgeText: {
    color: COLORS.orangeDark,
    fontSize: 7,
    fontWeight: "bold",
  },


  /* =======================================================
     PAYSLIP CARD
  ======================================================= */

  slipCard: {
    backgroundColor: COLORS.white,
    border: `1 solid ${COLORS.border}`,
    borderTop: `3 solid ${COLORS.orange}`,
    padding: 15,
    marginBottom: 16,
  },

  payslipHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },

  payslipTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: COLORS.charcoal,
  },

  payslipNumber: {
    backgroundColor: COLORS.orangeSoft,
    color: COLORS.orangeDark,
    paddingVertical: 5,
    paddingHorizontal: 8,
    fontSize: 8,
    fontWeight: "bold",
  },


  /* =======================================================
     INFORMATION BOX
  ======================================================= */

  infoBox: {
    backgroundColor: "#FAFAFA",
    border: `1 solid ${COLORS.borderLight}`,
    padding: 10,
    marginBottom: 13,
  },

  infoRow: {
    flexDirection: "row",
    marginBottom: 7,
  },

  infoRowLast: {
    flexDirection: "row",
  },

  infoLabel: {
    width: "30%",
    color: COLORS.muted,
    fontSize: 8,
  },

  infoValue: {
    width: "70%",
    color: COLORS.text,
    fontSize: 8,
    fontWeight: "bold",
  },


  /* =======================================================
     SECTION
  ======================================================= */

  sectionTitle: {
    backgroundColor: COLORS.orangeSoft,
    borderLeft: `3 solid ${COLORS.orange}`,
    color: COLORS.orangeDark,
    paddingVertical: 7,
    paddingHorizontal: 8,
    fontSize: 9,
    fontWeight: "bold",
    marginBottom: 8,
  },


  /* =======================================================
     SALARY TABLE
  ======================================================= */

  table: {
    width: "100%",
    border: `1 solid ${COLORS.border}`,
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: COLORS.charcoal,
    color: COLORS.white,
    paddingVertical: 7,
    paddingHorizontal: 8,
  },

  tableRow: {
    flexDirection: "row",
    paddingVertical: 7,
    paddingHorizontal: 8,
    borderBottom: `1 solid ${COLORS.borderLight}`,
    backgroundColor: COLORS.white,
  },

  tableRowAlt: {
    flexDirection: "row",
    paddingVertical: 7,
    paddingHorizontal: 8,
    borderBottom: `1 solid ${COLORS.borderLight}`,
    backgroundColor: "#FAFAFA",
  },

  description: {
    width: "65%",
  },

  amount: {
    width: "35%",
    textAlign: "right",
  },

  tableHeaderText: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.white,
  },

  tableCell: {
    fontSize: 8,
    color: COLORS.text,
  },


  /* =======================================================
     TOTAL
  ======================================================= */

  totalBox: {
    marginTop: 12,
    backgroundColor: COLORS.greenSoft,
    border: `1 solid #BBF7D0`,
    padding: 11,
  },

  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },

  totalLabel: {
    color: COLORS.green,
    fontSize: 9,
    fontWeight: "bold",
  },

  totalAmount: {
    color: COLORS.green,
    fontSize: 14,
    fontWeight: "bold",
  },


  /* =======================================================
     SECURITY / NOTE
  ======================================================= */

  paymentNotice: {
    marginTop: 10,
    backgroundColor: COLORS.orangeSoft,
    border: `1 solid #FED7AA`,
    padding: 9,
  },

  paymentNoticeText: {
    fontSize: 7.5,
    color: COLORS.orangeDark,
    lineHeight: 1.4,
  },


  /* =======================================================
     SIGNATURES
  ======================================================= */

  signatureArea: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 28,
    paddingTop: 5,
  },

  signature: {
    width: "40%",
    textAlign: "center",
  },

  signatureLine: {
    marginTop: 25,
    borderTop: `1 solid ${COLORS.charcoal}`,
  },

  signatureLabel: {
    marginTop: 5,
    fontSize: 7.5,
    color: COLORS.muted,
  },


  /* =======================================================
     PAYSLIP FOOTER
  ======================================================= */

  slipFooter: {
    marginTop: 16,
    paddingTop: 8,
    borderTop: `1 solid ${COLORS.borderLight}`,
    textAlign: "center",
    fontSize: 7,
    color: COLORS.muted,
  },


  /* =======================================================
     PAGE FOOTER
  ======================================================= */

  pageFooter: {
    position: "absolute",
    bottom: 12,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTop: `1 solid ${COLORS.border}`,
    paddingTop: 5,
  },

  pageFooterText: {
    fontSize: 6.5,
    color: COLORS.muted,
  },


  /* =======================================================
     SIGNING SHEET
  ======================================================= */

  sheetTitleArea: {
    backgroundColor: COLORS.white,
    borderTop: `4 solid ${COLORS.orange}`,
    borderBottom: `1 solid ${COLORS.border}`,
    padding: 13,
    marginBottom: 15,
  },

  sheetTitle: {
    textAlign: "center",
    fontSize: 15,
    fontWeight: "bold",
    color: COLORS.charcoal,
  },

  sheetSubtitle: {
    textAlign: "center",
    marginTop: 4,
    fontSize: 8,
    color: COLORS.muted,
  },

  sheetTable: {
    width: "100%",
    border: `1 solid ${COLORS.border}`,
  },

  sheetHeader: {
    flexDirection: "row",
    backgroundColor: COLORS.charcoal,
    paddingVertical: 8,
    paddingHorizontal: 6,
  },

  sheetRow: {
    flexDirection: "row",
    backgroundColor: COLORS.white,
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderBottom: `1 solid ${COLORS.borderLight}`,
  },

  sheetRowAlt: {
    flexDirection: "row",
    backgroundColor: "#FAFAFA",
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderBottom: `1 solid ${COLORS.borderLight}`,
  },

  nameCol: {
    width: "27%",
  },

  salaryCol: {
    width: "12%",
    textAlign: "right",
  },

  advanceCol: {
    width: "12%",
    textAlign: "right",
  },

  taxCol: {
    width: "10%",
    textAlign: "right",
  },

  socialCol: {
    width: "10%",
    textAlign: "right",
  },

  totalCol: {
    width: "12%",
    textAlign: "right",
  },

  signatureCol: {
    width: "17%",
    textAlign: "center",
  },

  sheetHeaderText: {
    color: COLORS.white,
    fontSize: 7.5,
    fontWeight: "bold",
  },

  sheetCellText: {
    color: COLORS.text,
    fontSize: 7.5,
  },

  signaturePlaceholder: {
    color: COLORS.muted,
    fontSize: 7,
  },


  /* =======================================================
     SIGNING SHEET SUMMARY
  ======================================================= */

  sheetSummary: {
    marginTop: 12,
    backgroundColor: COLORS.white,
    border: `1 solid ${COLORS.border}`,
    padding: 10,
  },

  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },

  summaryLabel: {
    fontSize: 8,
    color: COLORS.muted,
  },

  summaryValue: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.charcoal,
  },


  /* =======================================================
     EMPTY STATE
  ======================================================= */

  emptyBox: {
    backgroundColor: COLORS.white,
    border: `1 solid ${COLORS.border}`,
    padding: 25,
    textAlign: "center",
  },

  emptyTitle: {
    fontSize: 11,
    fontWeight: "bold",
    color: COLORS.charcoal,
  },

  emptyText: {
    marginTop: 6,
    fontSize: 8,
    color: COLORS.muted,
  },

});


/* =========================================================
   SAFE MONEY FORMATTER
========================================================= */

const money = (value) => {

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "$0.00";
  }

  return `$${numericValue.toFixed(2)}`;
};


/* =========================================================
   SAFE TEXT
========================================================= */

const safeText = (value, fallback = "-") => {

  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    return fallback;
  }

  return String(value);
};


/* =========================================================
   EMPLOYEE NAME
========================================================= */

const getEmployeeName = (payroll) => {

  return (
    payroll?.teacher?.full_name ||
    payroll?.support_staff_name ||
    payroll?.employee_name ||
    "-"
  );
};


/* =========================================================
   PAYROLL ROWS
========================================================= */

const getPayrollRows = (payroll) => {

  return [
    {
      label: "Basic Salary",
      value: payroll?.basic_salary,
    },

    {
      label: "Bonus / Compensation",
      value: payroll?.night_school_commission,
    },

    {
      label: "Salary Advance",
      value: payroll?.salary_advance
        ? -Math.abs(Number(payroll.salary_advance))
        : 0,
    },

    {
      label: "Income Tax",
      value: payroll?.income_tax
        ? -Math.abs(Number(payroll.income_tax))
        : 0,
    },

    {
      label: "Social Security",
      value: payroll?.social_security_tax
        ? -Math.abs(Number(payroll.social_security_tax))
        : 0,
    },

    {
      label: "Total Deduction",
      value: payroll?.total_deductions,
    },
  ];
};


/* =========================================================
   PAYROLL SLIP
========================================================= */

const PayrollSlip = ({
  payroll,
  index,
  month,
  academicYear,
}) => {

  const employeeName = getEmployeeName(payroll);

  const payrollRows = getPayrollRows(payroll);

  return (
    <View
      style={styles.slipCard}
      wrap
    >

      <View style={styles.payslipHeader}>

        <View>

          <Text style={styles.payslipTitle}>
            Monthly Payroll Statement
          </Text>

          <Text style={styles.schoolSubtitle}>
            Official Employee Payslip
          </Text>

        </View>


        <View style={styles.payslipNumber}>

          <Text>
            PAYSLIP #{index + 1}
          </Text>

        </View>

      </View>


      {/* EMPLOYEE INFORMATION */}

      <Text style={styles.sectionTitle}>
        EMPLOYEE INFORMATION
      </Text>


      <View style={styles.infoBox}>

        <View style={styles.infoRow}>

          <Text style={styles.infoLabel}>
            Employee Name
          </Text>

          <Text style={styles.infoValue}>
            {employeeName}
          </Text>

        </View>


        <View style={styles.infoRow}>

          <Text style={styles.infoLabel}>
            Payroll Period
          </Text>

          <Text style={styles.infoValue}>
            {safeText(month)} {safeText(academicYear)}
          </Text>

        </View>


        <View style={styles.infoRowLast}>

          <Text style={styles.infoLabel}>
            Employee Status
          </Text>

          <Text style={styles.infoValue}>
            Payroll Record
          </Text>

        </View>

      </View>


      {/* SALARY BREAKDOWN */}

      <Text style={styles.sectionTitle}>
        SALARY BREAKDOWN
      </Text>


      <View style={styles.table} wrap>


        <View
          style={styles.tableHeader}
          fixed
        >

          <Text
            style={[
              styles.description,
              styles.tableHeaderText,
            ]}
          >
            Description
          </Text>

          <Text
            style={[
              styles.amount,
              styles.tableHeaderText,
            ]}
          >
            Amount
          </Text>

        </View>


        {payrollRows.map((item, rowIndex) => (

          <View
            key={`${payroll?.id || index}-${rowIndex}`}
            style={
              rowIndex % 2 === 0
                ? styles.tableRow
                : styles.tableRowAlt
            }
            wrap={false}
          >

            <Text style={[styles.description, styles.tableCell]}>
              {item.label}
            </Text>

            <Text style={[styles.amount, styles.tableCell]}>
              {money(item.value)}
            </Text>

          </View>

        ))}


      </View>


      {/* NET PAY */}

      <View
        style={styles.totalBox}
        wrap={false}
      >

        <View style={styles.totalRow}>

          <Text style={styles.totalLabel}>
            NET SALARY PAID
          </Text>

          <Text style={styles.totalAmount}>
            {money(payroll?.total_paid)}
          </Text>

        </View>

      </View>


      {/* PAYMENT NOTICE */}

      <View
        style={styles.paymentNotice}
        wrap={false}
      >

        <Text style={styles.paymentNoticeText}>
          This payroll statement is an official institutional record
          of the salary amount processed for the stated payroll period.
          Please retain this document for your records.
        </Text>

      </View>


      {/* SIGNATURES */}

      <View
        style={styles.signatureArea}
        wrap={false}
      >

        <View style={styles.signature}>

          <Text>
            Employee Signature
          </Text>

          <View style={styles.signatureLine} />

          <Text style={styles.signatureLabel}>
            Date
          </Text>

        </View>


        <View style={styles.signature}>

          <Text>
            Authorized Signature
          </Text>

          <View style={styles.signatureLine} />

          <Text style={styles.signatureLabel}>
            Date
          </Text>

        </View>

      </View>


      <Text style={styles.slipFooter}>
        Official payroll document • Ignite Skill Center
      </Text>

    </View>
  );
};


/* =========================================================
   SIGNING SHEET
========================================================= */

const PayrollSigningSheet = ({
  payrolls,
  month,
  academicYear,
}) => {

  return (
    <Page
      size="A4"
      orientation="landscape"
      style={styles.landscapePage}
    >

      {/* HEADER */}

      <View style={styles.sheetTitleArea}>

        <Text style={styles.sheetTitle}>
          Ignite Skill Center
        </Text>

        <Text style={styles.sheetSubtitle}>
          Monthly Salary Signing Sheet • {safeText(month)}{" "}
          {safeText(academicYear)}
        </Text>

      </View>


      {/* TABLE */}

      {payrolls.length > 0 ? (

        <View style={styles.sheetTable}>

          {/* TABLE HEADER */}

          <View
            style={styles.sheetHeader}
            fixed
          >

            <Text
              style={[
                styles.nameCol,
                styles.sheetHeaderText,
              ]}
            >
              Staff Name
            </Text>

            <Text
              style={[
                styles.salaryCol,
                styles.sheetHeaderText,
              ]}
            >
              Salary
            </Text>

            <Text
              style={[
                styles.advanceCol,
                styles.sheetHeaderText,
              ]}
            >
              Advance
            </Text>

            <Text
              style={[
                styles.taxCol,
                styles.sheetHeaderText,
              ]}
            >
              Tax
            </Text>

            <Text
              style={[
                styles.socialCol,
                styles.sheetHeaderText,
              ]}
            >
              Social
            </Text>

            <Text
              style={[
                styles.totalCol,
                styles.sheetHeaderText,
              ]}
            >
              Total
            </Text>

            <Text
              style={[
                styles.signatureCol,
                styles.sheetHeaderText,
              ]}
            >
              Signature
            </Text>

          </View>


          {/* TABLE ROWS */}

          {payrolls.map((payroll, index) => {

            const employeeName = getEmployeeName(payroll);

            return (

              <View
                key={payroll?.id || `payroll-${index}`}
                style={
                  index % 2 === 0
                    ? styles.sheetRow
                    : styles.sheetRowAlt
                }
                wrap={false}
              >

                <Text
                  style={[
                    styles.nameCol,
                    styles.sheetCellText,
                  ]}
                >
                  {employeeName}
                </Text>


                <Text
                  style={[
                    styles.salaryCol,
                    styles.sheetCellText,
                  ]}
                >
                  {money(payroll?.basic_salary)}
                </Text>


                <Text
                  style={[
                    styles.advanceCol,
                    styles.sheetCellText,
                  ]}
                >
                  {money(payroll?.salary_advance)}
                </Text>


                <Text
                  style={[
                    styles.taxCol,
                    styles.sheetCellText,
                  ]}
                >
                  {money(payroll?.income_tax)}
                </Text>


                <Text
                  style={[
                    styles.socialCol,
                    styles.sheetCellText,
                  ]}
                >
                  {money(payroll?.social_security_tax)}
                </Text>


                <Text
                  style={[
                    styles.totalCol,
                    styles.sheetCellText,
                  ]}
                >
                  {money(payroll?.total_paid)}
                </Text>


                <View style={styles.signatureCol}>

                  <Text style={styles.signaturePlaceholder}>
                    __________________
                  </Text>

                </View>

              </View>

            );
          })}

        </View>

      ) : (

        <View style={styles.emptyBox}>

          <Text style={styles.emptyTitle}>
            No Payroll Records
          </Text>

          <Text style={styles.emptyText}>
            There are no payroll records available for this
            payroll period.
          </Text>

        </View>

      )}


      {/* SUMMARY */}

      {payrolls.length > 0 && (

        <View
          style={styles.sheetSummary}
          wrap={false}
        >

          <View style={styles.summaryRow}>

            <Text style={styles.summaryLabel}>
              Total Employees
            </Text>

            <Text style={styles.summaryValue}>
              {payrolls.length}
            </Text>

          </View>

        </View>

      )}


      {/* FOOTER */}

      <View
        style={styles.pageFooter}
        fixed
      >

        <Text style={styles.pageFooterText}>
          Ignite Skill Center • Official Payroll Record
        </Text>

        <Text
          style={styles.pageFooterText}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
        />

      </View>

    </Page>
  );
};


/* =========================================================
   MAIN PDF DOCUMENT
========================================================= */

function PayrollSlipGenerationPDF({
  payrolls = [],
  month = "",
  academicYear = "",
}) {

  const safePayrolls = Array.isArray(payrolls)
    ? payrolls.filter(Boolean)
    : [];


  return (

    <Document
      title={`Ignite Skill Center - Payroll - ${month} ${academicYear}`}
      author="Ignite Skill Center"
      subject="Official Monthly Payroll Statement"
      creator="Ignite Skill Center Management System"
      producer="Ignite Skill Center"
    >


      {/* ===================================================
          PAYSLIPS
      =================================================== */}

      {safePayrolls.length > 0 ? (

        <Page
          size="A4"
          style={styles.page}
          wrap
        >

          {/* DOCUMENT HEADER */}

          <View
            style={styles.documentHeader}
            fixed
          >

            <View style={styles.headerTop}>

              <View>

                <Text style={styles.schoolName}>
                  Ignite Skill Center
                </Text>

                <Text style={styles.schoolSubtitle}>
                  Official Monthly Payroll Statement
                </Text>

              </View>


              <View style={styles.officialBadge}>

                <Text style={styles.officialBadgeText}>
                  OFFICIAL PAYROLL RECORD
                </Text>

              </View>

            </View>

          </View>


          {/* PAYROLL SLIPS */}

          {safePayrolls.map((payroll, index) => (

            <PayrollSlip
              key={
                payroll?.id ||
                `payroll-${index}`
              }
              payroll={payroll}
              index={index}
              month={month}
              academicYear={academicYear}
            />

          ))}


          {/* PAGE FOOTER */}

          <View
            style={styles.pageFooter}
            fixed
          >

            <Text style={styles.pageFooterText}>
              Ignite Skill Center • Confidential Institutional Record
            </Text>

            <Text
              style={styles.pageFooterText}
              render={({ pageNumber, totalPages }) =>
                `Page ${pageNumber} of ${totalPages}`
              }
            />

          </View>

        </Page>

      ) : (

        <Page
          size="A4"
          style={styles.page}
        >

          <View style={styles.documentHeader}>

            <Text style={styles.schoolName}>
              Ignite Skill Center
            </Text>

            <Text style={styles.schoolSubtitle}>
              Official Monthly Payroll Statement
            </Text>

          </View>


          <View style={styles.emptyBox}>

            <Text style={styles.emptyTitle}>
              No Payroll Records
            </Text>

            <Text style={styles.emptyText}>
              No payroll records are available for{" "}
              {safeText(month)} {safeText(academicYear)}.
            </Text>

          </View>

        </Page>

      )}


      {/* ===================================================
          SIGNING SHEET
      =================================================== */}

      {safePayrolls.length > 0 && (

        <PayrollSigningSheet
          payrolls={safePayrolls}
          month={month}
          academicYear={academicYear}
        />

      )}

    </Document>

  );
}


export default PayrollSlipGenerationPDF;

